import { html, css, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { PageElement } from "../lib/page-element.js";
import { shared } from "../styles.js";
import { pb } from "../api/client.js";
import { humanError } from "../api/errors.js";
import { navigate } from "../nav.js";
import {
  getTeamBySlug,
  listMembers,
  invite,
  updateRole,
  removeMember,
  deleteTeam,
  myRoleIn,
} from "../api/teams.js";
import { ROLE_LABELS, roleAtLeast, assignableRoles, type Role } from "../lib/roles.js";
import type { TeamRecord, TeamMemberRecord } from "../api/registry.js";

/**
 * page-team manages one team and its roster (security-teams §2/§4), role-aware:
 * viewers/members see the roster read-only; maintainers invite, change roles
 * (below owner) and remove members; owners additionally manage owners, edit the
 * team and — for non-personal teams — delete it. The server hooks re-check
 * everything; this page only hides what would be rejected anyway. Team creation
 * lives on /teams.
 */
@customElement("page-team")
export class PageTeam extends PageElement {
  static override styles = [
    shared,
    css`
      .scope-link {
        margin: -6px 0 var(--sp-3);
      }
      .two {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--sp-3);
        align-items: end;
      }
      td.actions {
        text-align: right;
        white-space: nowrap;
      }
      td.actions select {
        width: auto;
        margin-right: 8px;
      }
      .danger {
        border-color: var(--danger);
      }
      .confirm-input {
        max-width: 240px;
      }
      @media (max-width: 560px) {
        .two {
          grid-template-columns: 1fr;
        }
      }
    `,
  ];

  @property() slug = "";

  @state() private team: TeamRecord | null = null;
  @state() private members: TeamMemberRecord[] = [];
  @state() private myRole: Role | null = null;
  @state() private errorMsg = "";
  @state() private notice = "";

  // forms
  @state() private inviteQ = "";
  @state() private inviteRole: Role = "member";
  @state() private deleteConfirm = "";
  @state() private busy = false;

  override willUpdate(changed: PropertyValues) {
    if (changed.has("slug")) void this.load();
  }

  private async load() {
    if (!this.slug) {
      navigate("/teams", { replace: true });
      return;
    }
    this.setTitle(`Team ${this.slug}`);
    this.errorMsg = "";
    try {
      this.team = await getTeamBySlug(this.slug);
      this.members = await listMembers(this.team.id);
      this.myRole = myRoleIn(this.members);
    } catch (e) {
      this.errorMsg = humanError(e);
    }
  }

  private async invite(e: Event) {
    e.preventDefault();
    if (!this.team) return;
    this.errorMsg = "";
    this.notice = "";
    this.busy = true;
    try {
      const user = await invite(this.team.id, this.inviteQ, this.inviteRole);
      this.notice = `Added @${user.handle} as ${ROLE_LABELS[this.inviteRole]}.`;
      this.inviteQ = "";
      await this.load();
    } catch (err) {
      this.errorMsg = humanError(err);
    } finally {
      this.busy = false;
    }
  }

  private async changeRole(m: TeamMemberRecord, role: Role) {
    if (role === m.role) return;
    this.errorMsg = "";
    this.notice = "";
    try {
      await updateRole(m.id, role);
      await this.load();
    } catch (e) {
      this.errorMsg = humanError(e);
      await this.load(); // reset the select to the stored role
    }
  }

  private async kick(m: TeamMemberRecord) {
    this.errorMsg = "";
    this.notice = "";
    try {
      await removeMember(m.id);
      await this.load();
    } catch (e) {
      this.errorMsg = humanError(e);
    }
  }

  private async removeTeam(e: Event) {
    e.preventDefault();
    if (!this.team || this.deleteConfirm.trim() !== this.team.name) {
      this.errorMsg = "Type the team name to confirm deletion.";
      return;
    }
    this.errorMsg = "";
    this.busy = true;
    try {
      await deleteTeam(this.team.id);
      navigate("/teams");
    } catch (err) {
      this.errorMsg = humanError(err);
    } finally {
      this.busy = false;
    }
  }

  /** rowEditable: can I change/remove this member at all? */
  private rowEditable(m: TeamMemberRecord): boolean {
    if (!this.team) return false;
    if (this.team.personal_for && this.team.personal_for === m.user) return false; // permanent owner
    if (m.role === "owner") return roleAtLeast(this.myRole, "owner");
    return roleAtLeast(this.myRole, "maintainer");
  }

  override render() {
    const isPersonal = !!this.team?.personal_for;
    const canManage = roleAtLeast(this.myRole, "maintainer");
    const canDelete = roleAtLeast(this.myRole, "owner") && !isPersonal;
    return html`
      <div class="wrap-narrow">
        <h1>
          ${this.team ? this.team.display_name || this.team.name : this.slug}
          ${isPersonal ? html`<span class="chip">Personal team</span>` : ""}
        </h1>
        ${this.team
          ? html`<p class="muted scope-link">
              <a class="mono" href=${`/scopes/${this.team.name}`}>View scope @${this.team.name} →</a>
            </p>`
          : ""}
        ${this.errorMsg ? html`<div class="error" role="alert">${this.errorMsg}</div>` : ""}
        ${this.notice ? html`<div class="card" style="border-color:var(--pos)">${this.notice}</div>` : ""}
        ${this.team ? this.renderMembers(canManage) : ""}
        ${this.team && canManage ? this.renderInvite() : ""}
        ${this.team && canDelete ? this.renderDanger() : ""}
      </div>
    `;
  }

  private renderMembers(canManage: boolean) {
    const me = pb.authStore.record?.id;
    return html`
      <div class="card">
        <h2>Members</h2>
        ${this.members.length
          ? html`<div class="table-scroll">
              <table>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Role</th>
                  ${canManage ? html`<th></th>` : ""}
                </tr>
              </thead>
              <tbody>
                ${this.members.map((m) => this.renderMemberRow(m, canManage, me))}
              </tbody>
              </table>
            </div>`
          : html`<p class="muted">No members visible.</p>`}
      </div>
    `;
  }

  private renderMemberRow(m: TeamMemberRecord, canManage: boolean, me: string | undefined) {
    const user = m.expand?.user;
    const label = (user?.name as string | undefined) || (user?.handle as string | undefined) || m.user;
    const handle = user?.handle as string | undefined;
    const editable = canManage && this.rowEditable(m);
    const options = assignableRoles(this.myRole);
    return html`<tr>
      <td>
        ${label}
        ${handle ? html`<span class="mono muted">@${handle}</span>` : ""}
        ${m.user === me ? html`<span class="faint">(you)</span>` : ""}
      </td>
      <td>
        ${editable
          ? html`<select
              aria-label=${`Role of ${label}`}
              @change=${(e: Event) => this.changeRole(m, (e.target as HTMLSelectElement).value as Role)}
            >
              ${options.map(
                (r) => html`<option value=${r} ?selected=${r === m.role}>${ROLE_LABELS[r]}</option>`,
              )}
            </select>`
          : html`<span class="chip">${ROLE_LABELS[m.role] ?? m.role}</span>`}
      </td>
      ${canManage
        ? html`<td class="actions">
            ${editable
              ? html`<button class="btn btn-danger" @click=${() => this.kick(m)}>Remove</button>`
              : ""}
          </td>`
        : ""}
    </tr>`;
  }

  private renderInvite() {
    return html`
      <div class="card">
        <h2>Add member</h2>
        <form @submit=${this.invite}>
          <div class="two">
            <div class="field">
              <label for="invite-q">Handle or email</label>
              <input
                id="invite-q"
                placeholder="jane_doe or jane@example.com"
                spellcheck="false"
                .value=${this.inviteQ}
                @input=${(e: Event) => (this.inviteQ = (e.target as HTMLInputElement).value)}
                required
              />
            </div>
            <div class="field">
              <label for="invite-role">Role</label>
              <select
                id="invite-role"
                @change=${(e: Event) => (this.inviteRole = (e.target as HTMLSelectElement).value as Role)}
              >
                ${assignableRoles(this.myRole).map(
                  (r) => html`<option value=${r} ?selected=${r === this.inviteRole}>${ROLE_LABELS[r]}</option>`,
                )}
              </select>
            </div>
          </div>
          <button class="btn btn-primary" type="submit" ?disabled=${this.busy}>
            ${this.busy ? "Adding…" : "Add member"}
          </button>
        </form>
      </div>
    `;
  }

  private renderDanger() {
    return html`
      <div class="card danger">
        <h2>Danger zone</h2>
        <p class="muted">
          Deleting the team removes its empty scopes. Teams that still own packages cannot be
          deleted.
        </p>
        <form @submit=${this.removeTeam}>
          <div class="field">
            <label for="delete-confirm">Type <span class="mono">${this.team?.name}</span> to confirm</label>
            <input
              id="delete-confirm"
              class="mono confirm-input"
              spellcheck="false"
              .value=${this.deleteConfirm}
              @input=${(e: Event) => (this.deleteConfirm = (e.target as HTMLInputElement).value)}
            />
          </div>
          <button class="btn btn-danger" type="submit" ?disabled=${this.busy}>Delete team</button>
        </form>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-team": PageTeam;
  }
}
